'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Zap,
  ArrowRight,
  Shield,
  ChevronDown,
  ChevronUp,
  X,
  Check,
  BookOpen,
  Target,
  Award,
  Brain,
  Wrench,
  DollarSign,
  Sparkles,
  GraduationCap,
  FlaskConical,
  Hand,
  Layers,
  Package,
  Rocket,
  LineChart,
  Repeat,
  Beaker,
  MousePointer,
  PencilLine,
} from 'lucide-react'
import { useRouter } from 'next/navigation'
import { createClient } from '@/lib/supabase/client'
import { buscarMeuPerfil } from '@/lib/perfil/meu-perfil'
import TransparentCheckout from '@/components/ui/transparent-checkout'
import { SiteFooter } from '@/components/ui/site-footer'

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: 'easeOut' as const },
  }),
}

const modulos = [
  { num: '0', titulo: 'Ativacao', aulas: 3, gratis: true, icon: GraduationCap, descricao: 'Porta de entrada gratuita. Quebra de crenca e os 6 pilares do metodo raiz.' },
  { num: '1', titulo: 'Fundacao Cientifica', aulas: 6, gratis: false, icon: Brain, descricao: 'Neurociencia do consumo. Peck, Boujbel, Ariely, Cialdini, Hormozi. Cada aula com paper citado.' },
  { num: '2', titulo: 'Arquitetura da Oferta Raiz', aulas: 7, gratis: false, icon: Package, descricao: 'Desenhar o produto antes de gastar R$1 em midia. Tangibilizacao, naming, Grand Slam, Unique Mechanism.' },
  { num: '3', titulo: 'Copy Raiz para Low Ticket', aulas: 7, gratis: false, icon: PencilLine, descricao: 'Schwartz, Hopkins, Belcher, Miller. Copy inversa ao marketing direto classico.' },
  { num: '4', titulo: 'Criativo Raiz', aulas: 6, gratis: false, icon: MousePointer, descricao: 'A filosofia do dedo na tela. 10 formatos que dao ROI 2+. Setup de R$200.' },
  { num: '5', titulo: 'Trafego Pago Raiz', aulas: 8, gratis: false, icon: Target, descricao: 'Saude da conta, teste em 3 dias, cluster pocket, escala vertical. O coracao operacional.' },
  { num: '6', titulo: 'Checkout, Bump e Pricing', aulas: 5, gratis: false, icon: DollarSign, descricao: 'Ariely aplicado. Ancoragem, decoy, regra de ouro do bump. Onde se destroi o ROI.' },
  { num: '7', titulo: 'Retencao e Habit Design', aulas: 4, gratis: false, icon: Repeat, descricao: 'Nir Eyal. A parte que ninguem no Brasil ensina. Hooked Model aplicado.' },
  { num: '8', titulo: 'Operacao e Escala', aulas: 5, gratis: false, icon: LineChart, descricao: 'Time minimo, SOPs de criativo, dashboard, compliance, ponte para trafego organico.' },
  { num: '9', titulo: 'Laboratorio', aulas: 4, gratis: false, icon: Beaker, descricao: 'Projeto final. Voce lanca seu primeiro low ticket ao vivo e faz a primeira venda.' },
]

const pilares = [
  { icon: Hand, titulo: 'Tangibilizacao', desc: 'Cartilha, nao ebook. Rotina, nao aula. Kit, nao modulo.' },
  { icon: Brain, titulo: 'Ruminacao mental', desc: 'O gatilho que o mercado brasileiro ignora. Dor ruminada converte.' },
  { icon: Zap, titulo: 'No-brain', desc: 'Compra no Sistema 1 antes da razao entrar em cena.' },
  { icon: PencilLine, titulo: 'Naming que come com o olho', desc: 'Nome concreto e evocativo. Construal Level Theory aplicada.' },
  { icon: MousePointer, titulo: 'Criativo de dedo na tela', desc: 'Mostrar o produto, nao a promessa. 1,3 segundo de atencao.' },
  { icon: Layers, titulo: 'Saude da conta', desc: 'Historico da conta pesa mais que qualquer atualizacao de algoritmo.' },
]

const faq = [
  {
    pergunta: 'Preciso ter experiencia com trafego pago para comecar?',
    resposta: 'Nao. O Modulo 5 ensina do zero: como abrir conta, subir o primeiro criativo, ler os primeiros sinais. Se voce ja tem experiencia, vai aprender as estruturas especificas do low ticket que nenhum curso generico ensina.',
  },
  {
    pergunta: 'Isso e para quem ja tem audiencia ou para quem esta comecando?',
    resposta: 'Para os dois. Quem tem audiencia ja sabe o nicho que rumina e escala mais rapido. Quem esta comecando segue o metodo de validacao em 48 horas do Modulo 2 e testa antes de investir pesado. O metodo nao depende de seguidores.',
  },
  {
    pergunta: 'Qual a diferenca para o curso de Funil Evergreen da eBuz?',
    resposta: 'O Funil Evergreen ensina funis completos de webinar, VSL e carrinho aberto para produtos de alto ticket (R$497 a R$2.997). O Metodo Low Ticket ensina a logica inversa: produto de R$27 a R$97 com pagina enxuta, checkout direto e criativo tangibilizado. Os dois cursos se conversam e um puxa o outro como esteira natural.',
  },
  {
    pergunta: 'Quanto preciso ter de verba para comecar?',
    resposta: 'O metodo de teste da fase um funciona com R$30 a R$50 por dia durante 3 dias. Ou seja, entre R$90 e R$150 para validar o primeiro produto. Se nao rodar bem, voce para antes de queimar.',
  },
  {
    pergunta: 'O curso tem fundamentacao cientifica ou e so achismo de mercado?',
    resposta: 'O Modulo 1 inteiro e sobre a ciencia por tras do metodo: Need for Touch Scale de Peck e Childers, ruminacao mental de Boujbel e d\'Astous, Sistema 1 e 2 de Kahneman, ancoragem de Ariely, Value Equation de Hormozi, 7 gatilhos de Cialdini e Hooked Model de Nir Eyal. Cada aula cita a fonte.',
  },
  {
    pergunta: 'O acesso e vitalicio?',
    resposta: 'Sim. Uma vez matriculado, voce tem acesso a todas as 55 aulas, atualizacoes futuras e novos casos que forem incluidos no curso. Sem mensalidade e sem cobranca recorrente.',
  },
  {
    pergunta: 'Tem garantia?',
    resposta: 'Sim. Garantia incondicional de 7 dias. Se por qualquer motivo voce achar que o curso nao serve para voce, basta pedir reembolso por email. Devolvemos 100% do valor sem perguntas.',
  },
  {
    pergunta: 'Eu preciso saber programar para construir a pagina de vendas?',
    resposta: 'Nao. O Modulo 9 (Laboratorio) faz ponte direta com o curso Vibecoding PRO da eBuz e mostra como construir pagina e checkout usando IA. Se voce nao sabe nada de codigo, segue o passo a passo do Vibecoding PRO. Se ja sabe, ganha tempo.',
  },
]

const referencias = [
  'Peck, J. & Childers, T. (2003) - Individual Differences in Haptic Information Processing: The Need for Touch Scale',
  'Boujbel, L. & d\'Astous, A. (2015) - Mental Rumination: How Unwanted and Recurrent Thoughts Can Perturbate the Purchasing Behavior',
  'Kahneman, D. (2011) - Thinking, Fast and Slow',
  'Ariely, D. (2008) - Predictably Irrational: The Hidden Forces That Shape Our Decisions',
  'Cialdini, R. (1984) - Influence: The Psychology of Persuasion',
  'Hormozi, A. (2021) - $100M Offers: How To Make Offers So Good People Feel Stupid Saying No',
  'Schwartz, E. (1966) - Breakthrough Advertising',
  'Eyal, N. (2014) - Hooked: How to Build Habit-Forming Products',
  'Miller, D. (2017) - Building a StoryBrand',
  'Brown, T. - E5 Method (Marketing Funnel Automation)',
  'Liang, Y. & Qiu, L. (2017) - The Effect of Tangibility Cues on Purchase Intention for E-Books',
]

export default function LowTicketPage() {
  const [faqAberto, setFaqAberto] = useState<number | null>(null)
  const [showCheckout, setShowCheckout] = useState(false)
  const [checkoutError, setCheckoutError] = useState('')
  const [buyerName, setBuyerName] = useState<string | undefined>()
  const [buyerEmail, setBuyerEmail] = useState<string | undefined>()
  const [buyerPhone, setBuyerPhone] = useState<string | undefined>()
  const [buyerCpf, setBuyerCpf] = useState<string | undefined>()
  const router = useRouter()

  useEffect(() => {
    const supabase = createClient()
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setBuyerEmail(user.email || undefined)
        setBuyerName(user.user_metadata?.full_name || undefined)
        buscarMeuPerfil()
          .then(({ data: profile }) => {
            if (profile) {
              if (profile.full_name) setBuyerName(profile.full_name)
              if (profile.phone) setBuyerPhone(profile.phone)
              if (profile.document) setBuyerCpf(profile.document)
            }
          })
      }
    })
  }, [])

  function handleCTAClick(e: React.MouseEvent) {
    e.preventDefault()
    setShowCheckout(true)
    setCheckoutError('')
    setTimeout(() => {
      document.getElementById('checkout-section')?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }

  return (
    <main className="min-h-screen bg-gray-950 text-gray-100">
      {/* Header */}
      <header className="w-full py-4 px-6 border-b border-gray-800/60">
        <div className="mx-auto max-w-5xl flex items-center gap-2">
          <BookOpen className="h-6 w-6 text-emerald-400" />
          <span className="text-lg font-semibold text-white">eBuz</span>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-950/80 via-gray-950 to-gray-950" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[120px] -translate-y-1/2 translate-x-1/4" />

        <div className="relative mx-auto max-w-4xl px-4 pt-16 pb-20 md:pt-24 md:pb-28 text-center">
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 px-4 py-1.5 text-sm font-medium text-emerald-300 mb-6">
              <Zap className="h-3.5 w-3.5" />
              55 aulas - 10 modulos - ~12h de conteudo
            </span>
          </motion.div>

          <motion.h1
            className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={1}
          >
            Metodo Low Ticket
            <br />
            <span className="bg-gradient-to-r from-emerald-400 to-emerald-300 bg-clip-text text-transparent">
              Do zero ao primeiro ROI 2+
            </span>
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-10"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={2}
          >
            A formacao mais robusta do Brasil sobre produto digital de baixo ticket. Fundamentacao cientifica, players globais e operacao real em Meta Ads. Ensinado por Jonathan Machado.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={3}
          >
            <button
              onClick={handleCTAClick}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 hover:from-emerald-400 hover:to-emerald-500 transition-all"
            >
              Garantir minha vaga
              <ArrowRight className="h-5 w-5" />
            </button>
            <div className="text-center">
              <p className="text-3xl font-bold text-white">
                R$ 297<span className="text-lg font-normal text-gray-400">,00</span>
              </p>
              <p className="text-sm text-gray-500">ou 12x de R$ 29,22</p>
            </div>
          </motion.div>

          <motion.div
            className="flex items-center justify-center gap-6 mt-8 text-sm text-gray-500 flex-wrap"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={4}
          >
            <span className="flex items-center gap-1.5">
              <Shield className="h-4 w-4 text-emerald-500" />7 dias de garantia
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="h-4 w-4 text-emerald-500" />
              Acesso vitalicio
            </span>
            <span className="flex items-center gap-1.5">
              <Zap className="h-4 w-4 text-emerald-500" />
              Acesso imediato
            </span>
          </motion.div>
        </div>
      </section>

      {/* Quebra de mito */}
      <section className="bg-gray-900/50 border-y border-gray-800/60">
        <div className="mx-auto max-w-4xl px-4 py-16 md:py-20">
          <motion.h2
            className="text-2xl md:text-3xl font-bold text-white text-center mb-4"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
          >
            Voce ja ouviu que low ticket nao da ROI.
          </motion.h2>
          <motion.p
            className="text-lg text-gray-400 text-center max-w-2xl mx-auto mb-10"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
          >
            Que voce precisa de uma esteira inteira, de lancamento, de webinar, de VSL, de tudo. Que produto de R$67 e pra quem esta comecando e quer queimar conta de anuncio.
          </motion.p>
          <motion.p
            className="text-lg text-emerald-300 text-center max-w-2xl mx-auto font-medium"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={2}
          >
            Mentira. Low ticket feito do jeito raiz bate ROI 2 ou mais, de forma sustentavel, como canal de aquisicao principal da sua operacao.
          </motion.p>
        </div>
      </section>

      {/* 6 Pilares */}
      <section className="mx-auto max-w-5xl px-4 py-16 md:py-20">
        <motion.h2
          className="text-2xl md:text-3xl font-bold text-white text-center mb-4"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          Os 6 pilares do Low Ticket Raiz
        </motion.h2>
        <motion.p
          className="text-gray-400 text-center max-w-2xl mx-auto mb-12"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
        >
          Todo low ticket que da ROI 2+ passa por essas 6 colunas. Todo que quebra ignora pelo menos uma delas.
        </motion.p>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {pilares.map((item, i) => (
            <motion.div
              key={item.titulo}
              className="rounded-2xl bg-gray-800/40 border border-gray-700/40 p-6"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={i + 1}
            >
              <item.icon className="h-7 w-7 text-emerald-400 mb-3" />
              <p className="font-semibold text-white mb-1">{item.titulo}</p>
              <p className="text-sm text-gray-400">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Modulos */}
      <section className="bg-gray-900/50 border-y border-gray-800/60">
        <div className="mx-auto max-w-5xl px-4 py-16 md:py-20">
          <motion.h2
            className="text-2xl md:text-3xl font-bold text-white text-center mb-4"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
          >
            10 modulos, 55 aulas, 1 metodo completo
          </motion.h2>
          <motion.p
            className="text-gray-400 text-center max-w-2xl mx-auto mb-12"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
          >
            Do paper cientifico ao criativo no ar. Cada modulo entrega um artefato tangivel e se conecta com os outros cursos da eBuz (Funil Evergreen, Vibecoding PRO, Pre-SEO/GEO).
          </motion.p>
          <div className="grid gap-4 md:grid-cols-2">
            {modulos.map((mod, i) => (
              <motion.div
                key={mod.num}
                className="rounded-2xl bg-gray-800/40 border border-gray-700/40 p-6 hover:border-emerald-500/40 transition-colors"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i + 1}
              >
                <div className="flex items-start gap-4">
                  <div className="shrink-0 rounded-xl bg-emerald-500/10 border border-emerald-500/30 w-12 h-12 flex items-center justify-center">
                    <mod.icon className="h-6 w-6 text-emerald-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-emerald-400">M{mod.num}</span>
                      {mod.gratis && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded">
                          Gratis
                        </span>
                      )}
                      <span className="text-xs text-gray-500">{mod.aulas} aulas</span>
                    </div>
                    <p className="font-semibold text-white mb-1">{mod.titulo}</p>
                    <p className="text-sm text-gray-400">{mod.descricao}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Base Cientifica */}
      <section className="mx-auto max-w-4xl px-4 py-16 md:py-20">
        <motion.div
          className="flex items-center justify-center gap-3 mb-4"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          <FlaskConical className="h-7 w-7 text-emerald-400" />
          <h2 className="text-2xl md:text-3xl font-bold text-white">Base cientifica real</h2>
        </motion.div>
        <motion.p
          className="text-gray-400 text-center max-w-2xl mx-auto mb-10"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
        >
          Nao e achismo de mercado. Cada tecnica ensinada tem respaldo em paper publicado ou livro classico de direct response e economia comportamental.
        </motion.p>
        <motion.div
          className="rounded-2xl bg-gray-800/40 border border-gray-700/40 p-6 md:p-8"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={2}
        >
          <ul className="space-y-3">
            {referencias.map((ref, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-gray-400">
                <Award className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>{ref}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </section>

      {/* Diferenciais */}
      <section className="bg-gray-900/50 border-y border-gray-800/60">
        <div className="mx-auto max-w-5xl px-4 py-16 md:py-20">
          <motion.h2
            className="text-2xl md:text-3xl font-bold text-white text-center mb-12"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
          >
            Por que este curso e diferente
          </motion.h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: FlaskConical, titulo: 'Fundamentacao cientifica', desc: 'Modulo 1 inteiro sobre a neurociencia por tras do metodo. Cada aula cita a fonte.' },
              { icon: Wrench, titulo: 'Metodo operacional', desc: 'Estruturas de campanha, cluster pocket, protocolo de escala. Nao e teoria.' },
              { icon: Hand, titulo: 'Tangibilizacao real', desc: 'Cartilha, rotina, kit, planilha. O formato que muda a conversao.' },
              { icon: Target, titulo: 'Criativo passo a passo', desc: '10 formatos que dao ROI 2+, com roteiro pronto e exemplos.' },
              { icon: Repeat, titulo: 'Retencao e habit design', desc: 'Nir Eyal aplicado. A parte que ninguem no Brasil ensina em low ticket.' },
              { icon: Rocket, titulo: 'Laboratorio ao vivo', desc: 'Modulo 9 e pratica pura. Voce sai com o primeiro produto no ar.' },
            ].map((item, i) => (
              <motion.div
                key={item.titulo}
                className="rounded-2xl bg-gray-800/40 border border-gray-700/40 p-6"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i + 1}
              >
                <item.icon className="h-7 w-7 text-emerald-400 mb-3" />
                <p className="font-semibold text-white mb-1">{item.titulo}</p>
                <p className="text-sm text-gray-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing + Checkout */}
      <section className="bg-gray-900/50 border-y border-gray-800/60">
        <div className="mx-auto max-w-lg px-4 py-16 md:py-20 text-center">
          <motion.h2
            className="text-2xl md:text-3xl font-bold text-white mb-2"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
          >
            Comece agora
          </motion.h2>
          <motion.p
            className="text-gray-400 mb-8"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
          >
            Acesso vitalicio. Sem mensalidade. Atualizacoes incluidas.
          </motion.p>
          <motion.div
            className="rounded-2xl bg-gray-800/60 border-2 border-emerald-500/40 p-8 mb-6"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={2}
          >
            <p className="text-sm text-emerald-400 font-medium mb-2">Metodo Low Ticket</p>
            <p className="text-4xl font-bold text-white mb-1">
              R$ 297<span className="text-lg font-normal text-gray-400">,00</span>
            </p>
            <p className="text-sm text-gray-500 mb-6">ou 12x de R$ 29,22 no cartao</p>
            <ul className="text-left space-y-3 mb-8">
              {[
                '55 aulas em 10 modulos',
                'Acesso vitalicio + atualizacoes',
                'Modulo 0 (3 aulas) gratuito',
                'Audio narrado por Jonathan Machado',
                'Material de apoio por aula',
                'Garantia incondicional de 7 dias',
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-gray-300">
                  <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <button
              onClick={handleCTAClick}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 hover:from-emerald-400 hover:to-emerald-500 transition-all"
            >
              Comecar agora
              <ArrowRight className="h-5 w-5" />
            </button>
            <div className="flex items-center justify-center gap-4 mt-4 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <Shield className="h-3.5 w-3.5" />
                Pagamento seguro
              </span>
              <span>PIX, cartao ou boleto</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Checkout */}
      {showCheckout && (
        <section id="checkout-section" className="mx-auto max-w-lg px-4 pb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Finalizar Compra</h2>
              <button
                onClick={() => setShowCheckout(false)}
                className="p-2 rounded-lg hover:bg-gray-800 transition-colors"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            {checkoutError && (
              <div className="mb-4 p-3 bg-red-950/50 border border-red-800 rounded-lg text-sm text-red-300">
                {checkoutError}
              </div>
            )}
            <TransparentCheckout
              channelSlug="low-ticket"
              buyerName={buyerName}
              buyerEmail={buyerEmail}
              buyerPhone={buyerPhone}
              buyerCpf={buyerCpf}
              onSuccess={(paymentId) => {
                router.push('/obrigado?payment=' + paymentId)
              }}
              onError={(error) => {
                setCheckoutError(error)
              }}
            />
          </motion.div>
        </section>
      )}

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-4 py-16 md:py-20">
        <motion.h2
          className="text-2xl md:text-3xl font-bold text-white text-center mb-10"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          Perguntas frequentes
        </motion.h2>
        <div className="space-y-3">
          {faq.map((item, i) => (
            <motion.div
              key={i}
              className="rounded-xl bg-gray-800/40 border border-gray-700/40 overflow-hidden"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={i * 0.3}
            >
              <button
                onClick={() => setFaqAberto(faqAberto === i ? null : i)}
                className="w-full flex items-center justify-between p-5 text-left"
              >
                <span className="font-medium text-white pr-4">{item.pergunta}</span>
                {faqAberto === i ? (
                  <ChevronUp className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {faqAberto === i && (
                <div className="px-5 pb-5 text-sm text-gray-400 leading-relaxed">{item.resposta}</div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Cross-sell outros cursos */}
      <section className="bg-gray-900/50 border-y border-gray-800/60">
        <div className="mx-auto max-w-4xl px-4 py-16 md:py-20 text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 px-4 py-1.5 text-sm font-medium text-emerald-300 mb-6">
              <Sparkles className="h-3.5 w-3.5" />
              Faz parte da esteira eBuz
            </span>
          </motion.div>
          <motion.h2
            className="text-2xl md:text-3xl font-bold text-white mb-4"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
          >
            Conecta com Funil Evergreen, Vibecoding PRO e SEO/GEO
          </motion.h2>
          <motion.p
            className="text-gray-400 max-w-2xl mx-auto"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={2}
          >
            Cada aula do Metodo Low Ticket traz referencias para os outros cursos da plataforma: frases, cases e metodologias compartilhadas. Quando voce quiser aprofundar pricing, funil de alto ticket, construcao tecnica da pagina ou trafego organico, o caminho ja esta tracado.
          </motion.p>
        </div>
      </section>

      <SiteFooter />
    </main>
  )
}
